import {
  Alert,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Text,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle } from '@tabler/icons-react'
import React from 'react'
import { useChaptersDelete } from '../../api/hooks/Chapters/useChaptersDelete'
import { ChapterItem, ChaptersDeleteData } from '../../types/chapters'

interface ChapterDeleteModalProps {
  opened: boolean
  onClose: () => void
  item: ChapterItem | null
}

const ChapterDeleteModal: React.FC<ChapterDeleteModalProps> = ({
  opened,
  onClose,
  item,
}) => {
  const { mutateAsync: deleteChapter, isPending } = useChaptersDelete()

  const handleDelete = async () => {
    if (!item) {
      notifications.show({
        title: 'Error',
        message: 'No item selected for deletion',
        color: 'red',
      })
      return
    }

    try {
      const deleteData: ChaptersDeleteData = {
        LEVEL_ID: item.LEVEL_ID,
      }

      await deleteChapter(deleteData)

      notifications.show({
        title: 'Success',
        message: 'Chapter deleted successfully',
        color: 'green',
      })

      onClose()
    } catch (error) {
      console.error('❌ 삭제 에러:', error)

      notifications.show({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to delete chapter',
        color: 'red',
      })
    }
  }

  const handleClose = () => {
    if (!isPending) {
      onClose()
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Delete Chapter"
      size="md"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isPending} />

        {item && (
          <>
            <Alert
              icon={<IconAlertTriangle size="1rem" />}
              title="Warning"
              color="red"
              mb="md"
            >
              This action cannot be undone. Are you sure you want to delete this
              chapter?
            </Alert>

            <div
              style={{
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '16px',
              }}
            >
              <Text size="sm" c="dimmed" mb="xs">
                Chapter Details:
              </Text>
              <Text>
                <strong>Level:</strong> {item.LEVEL}
              </Text>
              {item.MAIN && (
                <Text>
                  <strong>Main:</strong> {item.MAIN}
                </Text>
              )}
              {item.SUB && (
                <Text>
                  <strong>Sub:</strong> {item.SUB}
                </Text>
              )}
              {item.UNIT && (
                <Text>
                  <strong>Unit:</strong> {item.UNIT}
                </Text>
              )}
              {item.DETAIL_UNIT && (
                <Text>
                  <strong>Detail Unit:</strong> {item.DETAIL_UNIT}
                </Text>
              )}
            </div>

            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button color="red" onClick={handleDelete} loading={isPending}>
                Delete Chapter
              </Button>
            </Group>
          </>
        )}
      </div>
    </Modal>
  )
}

export default ChapterDeleteModal
